import {Flexidate, spawnTstzSpan} from "../tstzspan.ts";
import {useMemo} from "react";
import {isBefore} from "date-fns";
import {SortOrder, TruckOrVenueStub} from "../rpc/webrpc-client.gen.ts";
import {z} from "zod/v4";
import {TimelineTimeCoordinate, TimelineTimePoint, TimelineTimeSpan} from "./timelineTimeCoordinate.tsx";

export const BaseTimelineScene = z.object({
    when: TimelineTimeCoordinate,
    where: z.optional(TruckOrVenueStub),
    asof: z.optional(Flexidate),
});
export type BaseTimelineScene = z.infer<typeof BaseTimelineScene>;

export interface Scenes<SceneT extends BaseTimelineScene> {
    scenes: SceneT[];
}

// export const AnyTimelineSceneSchema = z.discriminatedUnion(
//     "subjectFlavor", [
//         BundleSceneSchema,
//         WasteSceneSchema,
//         JobSceneSchema,
//         TruckSceneSchema,
//         AgentSceneSchema,
//     ]);
// export type AnyTimelineScene = z.infer<typeof AnyTimelineSceneSchema>;

// export const timelineSceneDecoratorMap = {
//     bundle: {
//         sceneSchema: BundleSceneSchema,
//         diaryDecorator: decorateBundleSceneForDiary,
//         tileDecorator: decorateBundleSceneForTile,
//     },
//     waste: {
//         sceneSchema: WasteSceneSchema,
//         diaryDecorator: decorateWasteSceneForDiary,
//         tileDecorator: decorateWasteSceneForTile,
//     },
//     truck: {
//         sceneSchema: TruckSceneSchema,
//         diaryDecorator: decorateTruckSceneForDiary,
//         tileDecorator: decorateTruckSceneForTile,
//     },
//     agent: {
//         sceneSchema: AgentSceneSchema,
//         diaryDecorator: decorateAgentSceneForDiary,
//         tileDecorator: decorateAgentSceneForTile,
//     },
// } as const;

// export function decorateTimelineSceneForDiary(rawScene: TimelineScene): DiaryEntryProps | undefined {
//     const subjectFlavor = rawScene.subjectFlavor;
//     switch (subjectFlavor) {
//         case "bundle": {
//             const scene = BundleSceneSchema.parse(rawScene);
//             const rv = decorateBundleSceneForDiary(scene);
//             return rv;
//         }
//         case "waste": {
//             const scene = WasteSceneSchema.parse(rawScene);
//             const rv = decorateWasteSceneForDiary(scene);
//             return rv;
//         }
//         case "truck": {
//             const scene = TruckSceneSchema.parse(rawScene);
//             const rv = decorateTruckSceneForDiary(scene);
//             return rv;
//         }
//         case "agent": {
//             const scene = AgentSceneSchema.parse(rawScene);
//             const rv = decorateAgentSceneForDiary(scene);
//             return rv;
//         }
//     }
// }
//
// export function decorateTimelineSceneForTile(rawScene: TimelineScene): SceneTileContentProps | undefined {
//     const subjectFlavor = rawScene.subjectFlavor;
//     switch (subjectFlavor) {
//         case "bundle": {
//             const scene = BundleSceneSchema.parse(rawScene);
//             const rv = decorateBundleSceneForTile(scene);
//             return rv;
//         }
//         case "waste": {
//             const scene = WasteSceneSchema.parse(rawScene);
//             const rv = decorateWasteSceneForTile(scene);
//             return rv;
//         }
//         case "truck": {
//             const scene = TruckSceneSchema.parse(rawScene);
//             const rv = decorateTruckSceneForTile(scene);
//             return rv;
//         }
//         case "agent": {
//             const scene = AgentSceneSchema.parse(rawScene);
//             const rv = decorateAgentSceneForTile(scene);
//             return rv;
//         }
//     }
// }

//
// export function makeSceneDecoratorArgsSchema<SceneT extends z.ZodType>(sceneSchema: SceneT) {
//     return z.object({
//         t: TSchema,
//         scene: sceneSchema,
//         color: z.optional(JoyColorSchema),
//     });
// }

// const TimelineSceneViewRoot
//     = styled("div", {
//     name: "TimelineSceneView",
//     slot: "root",
// })<{ ownerState: TimelineSceneViewProps }>(() => {
//     return {
//         py: 2,
//         display: "flex",
//         flexDirection: "row",
//         columnGap: 3,
//         alignItems: "center",
//         width: "100%",
//     };
// });
//
// const TimelineSceneWhenView
//     = styled(Sheet, {
//     name: "TimelineSceneView",
//     slot: "when",
// })<{ ownerState: TimelineSceneViewProps }>(() => {
//     return {
//         width: "12rem",
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         justifyContent: "center",
//         borderRadius: "16px",
//         fontSize: "lg",
//     };
// });
//
// const TimelineSceneWhatView
//     = styled(Sheet, {
//     name: "TimelineSceneView",
//     slot: "what",
// })<{ ownerState: TimelineSceneViewProps }>(() => {
//     return {
//         flex: 1,
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "left",
//         borderRadius: "16px",
//         px: "12px",
//     };
// });

export function pointillize(ttc: TimelineTimeCoordinate): TimelineTimePoint {
    const atz = TimelineTimePoint.safeParse(ttc);
    if (atz.success) {
        return atz.data;
    }

    const spanz = TimelineTimeSpan.safeParse(ttc);
    if (spanz.success) {
        const span = spawnTstzSpan(spanz.data.span);
        const pt = span.guaranteedIncluded();
        if (pt) {
            return {at: pt};
        }
    }

    throw new Error(`cannot pointillize`, {cause: ttc});
}

export function cmpTimeCoords(a: TimelineTimeCoordinate | undefined, b: TimelineTimeCoordinate | undefined): number {
    if (!a) {
        if (!b) {
            return 0;
        } else {
            return 1;
        }
    }
    if (!b) {
        return -1;
    }
    const ad = pointillize(a).at;
    if (!ad) {
        return -1;
    }
    const bd = pointillize(b).at;
    if (!bd) {
        return 1;
    }
    if (isBefore(ad, bd)) {
        return -1;
    }
    if (isBefore(bd, ad)) {
        return 1;
    }
    return 0;
}

export interface TimeLocated {
    when?: TimelineTimeCoordinate;
}

export function cmpTimeCoordinatedAsc<T extends TimeLocated>(a: T, b: T) {
    return cmpTimeCoords(a.when, b.when);
}

export function cmpTimeCoordinatedDesc<T extends TimeLocated>(a: T, b: T) {
    return cmpTimeCoords(b.when, a.when);
}

export function useTimeCoordinatedComparator(sortOrder: SortOrder) {
    const cmp = useMemo(() => {
        return sortOrder === SortOrder.enum.ASC ? cmpTimeCoordinatedAsc : cmpTimeCoordinatedDesc;
    }, [sortOrder]);
    return cmp;
}

// export function makeTimelineTileContentPropsSchema<SceneSchemaT extends z.ZodType<typeof TimelineSceneBaseSchema>>(sceneSchema: SceneSchemaT) {
//     return JoyableSchema.extend({scene: sceneSchema});
// }
//

export function cmpScenesOldestFirst(a: BaseTimelineScene | null, b: BaseTimelineScene | null) {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return 1;
    }
    if (!b) {
        return -1;
    }
    return cmpTimeCoords(a.when, b.when);
}

export function cmpActorScenesNewestFirst<SceneT extends BaseTimelineScene>(
    a: { lastScene: SceneT | null },
    b: { lastScene: SceneT | null }) {
    const ascene = a.lastScene;
    const bscene = b.lastScene;
    return cmpScenesNewestFirst(ascene, bscene);
}

export function cmpScenesNewestFirst(a: BaseTimelineScene | null, b: BaseTimelineScene | null) {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return -1;
    }
    if (!b) {
        return 1;
    }
    return cmpTimeCoords(b.when, a.when);
}

